import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Switch } from '../../lib/shadcn/switch'
import { Textarea } from '../../lib/shadcn/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../lib/shadcn/select'
import { toast } from '../../lib/shadcn/sonner'
import {
  useDescribePhotoReference,
  useGetPhotoCleanupSettings,
  useUpdatePhotoCleanupSettings,
} from '../../hooks/backend/settingsPage'
import { useUploadProductPhoto } from '../../hooks/backend/inventoryPage'
import { HelpText } from '../../components/HelpText'

interface Settings {
  enabled: boolean
  prompt: string
  quality: string
  size: string
  reference_url: string | null
  reference_description: string | null
  combine_prompt: string
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

const DEFAULT_PROMPT = 'Clean up this product photo: remove the glare and straighten it up.'

const DEFAULT_COMBINE_PROMPT =
  'Combine these two photos into one product photo. Image 1 is the bag and image 2 is a pile of the real popcorn. ' +
  'Copy both exactly as photographed: do not redraw, restyle, smooth, brighten or add shine to the popcorn; keep every kernel\'s real texture, coating, color and size from image 2. ' +
  'Make the bag a bit smaller and place the popcorn pile from image 2 on the surface in front of the bottom of the bag. ' +
  'Keep the bag and its label exactly as in image 1. Plain white background with a soft natural shadow.'

// OpenAI's GPT image models take prompts up to 32,000 characters.
const MAX_PROMPT = 32000

const QUALITIES = [
  { value: 'medium', label: 'Medium (about 35 seconds)' },
  { value: 'low', label: 'Low (faster, less detail)' },
]

const SIZES = [
  { value: '1024x1024', label: 'Square (1024 × 1024)' },
  { value: '1024x1536', label: 'Tall (1024 × 1536)' },
]

export default function PhotoCleanupSettings() {
  const getSettings = useGetPhotoCleanupSettings()
  const updateSettings = useUpdatePhotoCleanupSettings()
  const uploadPhoto = useUploadProductPhoto()
  const describeReference = useDescribePhotoReference()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const data = getSettings.data as Settings | null | undefined

  const [draft, setDraft] = useState<Settings | null>(null)

  useEffect(() => {
    void getSettings.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data) setDraft({ ...data })
  }, [data])

  const patch = (p: Partial<Settings>) => setDraft(d => (d ? { ...d, ...p } : d))

  // Writes out the reference photo's style, which is what ChatGPT gets.
  const describe = async (url: string) => {
    try {
      const res = (await describeReference.trigger({ url }).result) as { description?: string } | undefined
      if (!res?.description) throw new Error('No description came back')
      patch({ reference_description: res.description })
      return true
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
      return false
    }
  }

  // Uploads the reference photo; it's kept once Save is clicked.
  const uploadReference = async (file: File | undefined) => {
    if (!file) return
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error('Use a PNG, JPG or WebP photo')
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('The photo is too large (20 MB at most)')
      return
    }
    try {
      const res = (await uploadPhoto.trigger({
        fileName: `reference-${file.name}`,
        data: await fileToBase64(file),
        mimeType: file.type,
      }).result) as { url?: string } | undefined
      if (!res?.url) throw new Error('No URL came back')
      patch({ reference_url: res.url, reference_description: null })
      if (await describe(res.url)) {
        toast.success('Reference photo uploaded and its style written out. Check it, then click Save.')
      }
    } catch (err) {
      toast.error(`Upload failed: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const save = async () => {
    if (!draft) return
    if (!draft.prompt.trim()) {
      toast.error('Enter a prompt for the photo cleanup')
      return
    }
    if (draft.prompt.trim().length > MAX_PROMPT) {
      toast.error(`The prompt is too long (${MAX_PROMPT.toLocaleString('en-US')} characters at most)`)
      return
    }
    if (!draft.combine_prompt?.trim()) {
      toast.error('Enter a prompt for Combine with AI')
      return
    }
    if (draft.combine_prompt.trim().length > MAX_PROMPT) {
      toast.error(`The combine prompt is too long (${MAX_PROMPT.toLocaleString('en-US')} characters at most)`)
      return
    }
    if (draft.reference_url && !draft.reference_description?.trim()) {
      toast.error('Describe the reference style (or remove the reference photo) before saving')
      return
    }
    try {
      await updateSettings.trigger({
        ...draft,
        prompt: draft.prompt.trim(),
        combine_prompt: draft.combine_prompt.trim(),
      })
      await getSettings.trigger({}, { skipCache: true })
      toast.success('Photo cleanup settings saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save photo cleanup settings')
    }
  }

  return (
    <Card id="photo-cleanup">
      <CardHeader>
        <CardTitle>Photo Cleanup</CardTitle>
        <CardDescription><HelpText>
          Product photos are cleaned up, or a bag and popcorn photo combined, with ChatGPT using the prompts below. The original is always
          kept, so you can put it back from the product dialog. ChatGPT redraws the photo, so check
          the label text and QR code on each cleaned-up photo before saving the product.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent>
        {getSettings.error && !data ? (
          <p className="text-sm text-destructive">Couldn't load photo cleanup settings.</p>
        ) : !draft ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2">
              <div>
                <Label htmlFor="photo-cleanup-enabled">Clean up new photos automatically</Label>
                <p className="text-xs text-muted-foreground">
                  When off, photos upload as they are and you can clean up one at a time.
                </p>
              </div>
              <Switch
                id="photo-cleanup-enabled"
                checked={draft.enabled}
                onCheckedChange={v => patch({ enabled: v })}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="photo-cleanup-prompt">Prompt</Label>
                {draft.prompt.trim() !== DEFAULT_PROMPT && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                    onClick={() => patch({ prompt: DEFAULT_PROMPT })}
                  >
                    Reset to default
                  </button>
                )}
              </div>
              <Textarea
                id="photo-cleanup-prompt"
                rows={8}
                value={draft.prompt}
                onChange={e => patch({ prompt: e.target.value })}
              />
              <p
                className={
                  draft.prompt.trim().length > MAX_PROMPT
                    ? 'text-right text-xs tabular-nums text-destructive'
                    : 'text-right text-xs tabular-nums text-muted-foreground'
                }
              >
                {draft.prompt.trim().length.toLocaleString('en-US')} / {MAX_PROMPT.toLocaleString('en-US')}
              </p>
              <p className="text-xs text-muted-foreground">
                Short, plain instructions work best. Asking for a "professional" or "new" photo tends
                to make ChatGPT redraw the label.
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="photo-combine-prompt">Combine with AI prompt</Label>
                {draft.combine_prompt.trim() !== DEFAULT_COMBINE_PROMPT && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                    onClick={() => patch({ combine_prompt: DEFAULT_COMBINE_PROMPT })}
                  >
                    Reset to default
                  </button>
                )}
              </div>
              <Textarea
                id="photo-combine-prompt"
                rows={6}
                value={draft.combine_prompt}
                onChange={e => patch({ combine_prompt: e.target.value })}
              />
              <p className="text-xs text-muted-foreground"><HelpText>
                Used by Combine with AI in the product dialog, which turns a bag photo and a loose
                popcorn photo into one photo. ChatGPT gets the bag as image 1 and the popcorn as
                image 2. The reference photo below isn&apos;t used for this.
              </HelpText></p>
            </div>

            <div className="space-y-1.5">
              <Label>Reference photo (optional)</Label>
              <div className="flex flex-wrap items-start gap-3">
                {draft.reference_url ? (
                  <a
                    href={draft.reference_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block h-28 w-28 shrink-0 overflow-hidden rounded-md border bg-muted"
                    title="Open the reference photo"
                  >
                    <img src={draft.reference_url} alt="Reference" className="h-full w-full object-cover" />
                  </a>
                ) : (
                  <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
                    None
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={e => void uploadReference(e.target.files?.[0])}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={uploadPhoto.loading || describeReference.loading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadPhoto.loading || describeReference.loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="h-4 w-4" />
                    )}
                    {uploadPhoto.loading
                      ? 'Uploading…'
                      : describeReference.loading
                        ? 'Reading style…'
                        : draft.reference_url
                          ? 'Replace photo'
                          : 'Upload photo'}
                  </Button>
                  {draft.reference_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-muted-foreground hover:text-destructive"
                      onClick={() => patch({ reference_url: null, reference_description: null })}
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground"><HelpText>
                A finished product shot whose background, lighting and framing you want matched. Its
                style is written out below and added to the prompt; the photo itself isn&apos;t sent to
                ChatGPT, so it can&apos;t swap in the reference&apos;s product.
              </HelpText></p>

              {draft.reference_url && (
                <div className="space-y-1.5 pt-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="photo-cleanup-reference-style">Reference style</Label>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
                      disabled={describeReference.loading}
                      onClick={() => draft.reference_url && void describe(draft.reference_url)}
                    >
                      <RefreshCw className={describeReference.loading ? 'h-3 w-3 animate-spin' : 'h-3 w-3'} />
                      {draft.reference_description ? 'Describe again' : 'Describe photo'}
                    </button>
                  </div>
                  <Textarea
                    id="photo-cleanup-reference-style"
                    rows={6}
                    value={draft.reference_description ?? ''}
                    onChange={e => patch({ reference_description: e.target.value })}
                    placeholder={describeReference.loading ? 'Reading the photo…' : 'Click Describe photo'}
                  />
                  <p className="text-xs text-muted-foreground">
                    Edit freely. Keep it about the look (background, light, shadow, angle, framing), not
                    the product.
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Quality</Label>
                <Select value={draft.quality} onValueChange={v => patch({ quality: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUALITIES.map(q => (
                      <SelectItem key={q.value} value={q.value}>
                        {q.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Photo size</Label>
                <Select value={draft.size} onValueChange={v => patch({ size: v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZES.map(s => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="button" onClick={() => void save()} disabled={updateSettings.loading} className="gap-1.5">
                {updateSettings.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
